import { TenantServiceResponseModel, Tenant, CreateTenantServiceModel, UpdateTenantServiceModel } from '../types';
import { detectFormChanges } from './changeDetector';

/**
 * Maps API tenant response to UI tenant model
 */
export const mapApiTenantToUi = (apiTenant: TenantServiceResponseModel): Tenant => {
  // Map status boolean to string
  const getStatusString = (isActive: boolean): 'Active' | 'Inactive' | 'Pending' | 'Maintenance' => {
    return isActive ? 'Active' : 'Inactive';
  };

  return {
    id: apiTenant.id,
    tenantId: apiTenant.id, // Using the same ID for tenantId
    code: apiTenant.code, // Include code from API response
    name: apiTenant.name,
    frontendServer: apiTenant.frontendUrl,
    backendApi: apiTenant.backendUrl,
    middlewareApi: apiTenant.middlewareUrl,
    status: getStatusString(apiTenant.isActive),
  };
};

/**
 * Maps UI tenant form data to API create model
 */
export const mapUiToCreateTenantApiModel = (formData: {
  code: string;
  name: string;
  frontendServer: string;
  backendApi: string;
  middlewareApi: string;
  status: 'Active' | 'Inactive' | 'Pending' | 'Maintenance';
}): CreateTenantServiceModel => {
  // Map status string to boolean
  const getIsActive = (status: string): boolean => {
    return status === 'Active';
  };

  return {
    name: formData.name,
    code: formData.code,
    description: `Tenant for ${formData.name}`,
    frontendUrl: formData.frontendServer,
    backendUrl: formData.backendApi,
    middlewareUrl: formData.middlewareApi,
    isActive: getIsActive(formData.status),
  };
};

/**
 * Maps UI tenant form data to API update model
 */
export const mapUiToUpdateTenantApiModel = (formData: {
  code: string;
  name: string;
  frontendServer: string;
  backendApi: string;
  middlewareApi: string;
  status: 'Active' | 'Inactive' | 'Pending' | 'Maintenance';
}): UpdateTenantServiceModel => {
  // Map status string to boolean
  const getIsActive = (status: string): boolean => {
    return status === 'Active';
  };

  return {
    name: formData.name,
    code: formData.code,
    description: `Tenant for ${formData.name}`,
    frontendUrl: formData.frontendServer,
    backendUrl: formData.backendApi,
    middlewareUrl: formData.middlewareApi,
    isActive: getIsActive(formData.status),
  };
};

/**
 * Maps UI tenant form data to API update model with only changed fields for PATCH requests
 */
export const mapUiToUpdateTenantApiModelPatch = (
  originalTenant: Tenant,
  formData: {
    code: string;
    name: string;
    frontendServer: string;
    backendApi: string;
    middlewareApi: string;
    status: 'Active' | 'Inactive' | 'Pending' | 'Maintenance';
  }
): UpdateTenantServiceModel => {
  // Map status string to boolean
  const getIsActive = (status: string): boolean => {
    return status === 'Active';
  };

  // Create the full update object
  const fullUpdate: UpdateTenantServiceModel = {
    name: formData.name,
    code: formData.code,
    description: `Tenant for ${formData.name}`,
    frontendUrl: formData.frontendServer,
    backendUrl: formData.backendApi,
    middlewareUrl: formData.middlewareApi,
    isActive: getIsActive(formData.status),
  };

  // Create the original object for comparison
  const originalApiModel: UpdateTenantServiceModel = {
    name: originalTenant.name,
    code: originalTenant.code || '',
    description: `Tenant for ${originalTenant.name}`,
    frontendUrl: originalTenant.frontendServer,
    backendUrl: originalTenant.backendApi,
    middlewareUrl: originalTenant.middlewareApi,
    isActive: getIsActive(originalTenant.status),
  };

  // Return only the changed fields
  return detectFormChanges(originalApiModel, fullUpdate);
};
